/**
 * Enhanced Product Card Component
 * Provides advanced variant selection with size pills, flavor dropdown, and quantity selector
 * Integrates with existing Eurus theme cart system
 */

if (!window.Eurus.loadedScript.has('card-product-enhanced.js')) {
  window.Eurus.loadedScript.add('card-product-enhanced.js');

  const registerComponent = () => {
    if (typeof Alpine === 'undefined') {
      console.error('Alpine is not defined! Cannot register xCardProductEnhanced');
      return;
    }

    /**
     * Enhanced Product Card Alpine Component
     * @param {Object} config - Configuration object
     * @param {string} config.productId - Product ID
     * @param {string} config.sectionId - Section ID
     * @param {string} config.productUrl - Product URL
     * @param {boolean} config.enableEnhanced - Enable enhanced variant UI
     * @param {boolean} config.showQuantity - Show quantity selector
     * @param {Array} config.variants - Product variants
     * @param {Array} config.options - Product options
     */
    Alpine.data('xCardProductEnhanced', (config) => ({
        // Configuration
        productId: config.productId,
        sectionId: config.sectionId,
        productUrl: config.productUrl,
        enableEnhanced: config.enableEnhanced,
        showQuantity: config.showQuantity,

        // Data
        variants: [],
        options: [],
        variantMatrix: {},

        // State
        selectedOption1: '',
        selectedOption2: '',
        selectedOption3: '',
        // Legacy aliases for backwards compatibility
        get selectedSize() { return this.selectedOption1; },
        set selectedSize(val) { this.selectedOption1 = val; },
        get selectedFlavor() { return this.selectedOption2; },
        set selectedFlavor(val) { this.selectedOption2 = val; },
        quantity: 1,
        selectedVariant: null,
        loading: false,
        errorMessage: '',
        // Selling plan state
        subscriptionFirst: config.subscriptionFirst !== false,
        purchaseType: config.subscriptionFirst !== false ? 'subscription' : 'onetime',
        selectedSellingPlan: '',
        subscriptionDiscountPercent: 0.20, // 20% discount for subscriptions
        isInitialLoad: true,

        /**
         * Get display price based on purchase type
         * Returns discounted price for subscriptions
         */
        get displayPrice() {
          if (!this.selectedVariant) return 0;

          if (this.purchaseType === 'subscription') {
            // Apply 20% discount for subscriptions
            return Math.round(this.selectedVariant.price * (1 - this.subscriptionDiscountPercent));
          }

          return this.selectedVariant.price;
        },

        /**
         * Get display compare_at_price based on purchase type
         * Returns discounted compare price for subscriptions
         */
        get displayCompareAtPrice() {
          if (!this.selectedVariant || !this.selectedVariant.compare_at_price) return null;

          if (this.purchaseType === 'subscription') {
            // Apply 20% discount for subscriptions
            return Math.round(this.selectedVariant.compare_at_price * (1 - this.subscriptionDiscountPercent));
          }

          return this.selectedVariant.compare_at_price;
        },

        /**
         * Initialize component
         */
        init() {
          // Parse JSON strings if needed
          this.variants = this._parseJSON(config.variants);
          this.options = this._parseJSON(config.options);

          // Build variant lookup matrix for faster variant matching
          this._buildVariantMatrix();

          // Initialize with first available variant
          this.selectedVariant = this._getFirstAvailableVariant() || this.variants[0];

          // Set initial selections based on selected variant
          if (this.enableEnhanced && this.options.length > 0) {
            this.selectedOption1 = this.selectedVariant?.option1 || this.options[0]?.values[0];

            if (this.options.length > 1) {
              this.selectedOption2 = this.selectedVariant?.option2 || this.options[1]?.values[0];
            }

            if (this.options.length > 2) {
              this.selectedOption3 = this.selectedVariant?.option3 || this.options[2]?.values[0];
            }

            this.updateSelectedVariant();
            this.$nextTick(() => this._updateVariantImage());
          }

          // Auto-select first selling plan on initial load if subscription is default and variant has selling plans
          if (this.isInitialLoad && this.purchaseType === 'subscription' && this.selectedVariant?.selling_plan_allocations?.length > 0) {
            this.isInitialLoad = false;
            this.selectedSellingPlan = this.selectedVariant.selling_plan_allocations[0].selling_plan_id;
          }

          // Create AbortController for automatic cleanup (if supported)
          if (typeof AbortController !== 'undefined') {
            this._abortController = new AbortController();
          }

          // Store listener reference for cleanup
          this._externalUpdateListener = (event) => {
            if (event.detail?.variant) {
              this.selectedVariant = event.detail.variant;
              this.selectedOption1 = event.detail.variant.option1;
              this.selectedOption2 = event.detail.variant.option2;
              this.selectedOption3 = event.detail.variant.option3;
            }
          };

          // Listen for external variant updates with AbortController (if supported)
          const listenerOptions = this._abortController
            ? { signal: this._abortController.signal }
            : false;

          document.addEventListener(
            `eurus:card-product:update:${this.productId}`,
            this._externalUpdateListener,
            listenerOptions
          );

          // Register cleanup with Alpine's destroy hook
          this.$el.addEventListener('alpine:destroying', () => {
            this.destroy();
          }, { once: true });

          // Watch for purchase type changes to auto-select first selling plan
          this.$watch('purchaseType', (value) => {
            if (value === 'subscription' && !this.selectedSellingPlan && this.selectedVariant.selling_plan_allocations?.length > 0) {
              this.selectedSellingPlan = this.selectedVariant.selling_plan_allocations[0].selling_plan_id;
            }
          });
        },

        /**
         * Parse JSON string or return object as-is
         */
        _parseJSON(data) {
          if (typeof data === 'string') {
            try {
              return JSON.parse(data);
            } catch (e) {
              console.error('Failed to parse JSON:', e);
              return [];
            }
          }
          return data || [];
        },

        /**
         * Get first available variant
         */
        _getFirstAvailableVariant() {
          return this.variants.find(v => v.available);
        },

        /**
         * Build variant matrix for O(1) variant lookup
         * Creates a nested object structure: { option1: { option2: { option3: variant } } }
         */
        _buildVariantMatrix() {
          this.variantMatrix = {};

          this.variants.forEach(variant => {
            const key1 = variant.option1 || '_default';
            const key2 = variant.option2 || '_default';
            const key3 = variant.option3 || '_default';

            if (!this.variantMatrix[key1]) {
              this.variantMatrix[key1] = {};
            }

            if (!this.variantMatrix[key1][key2]) {
              this.variantMatrix[key1][key2] = {};
            }

            this.variantMatrix[key1][key2][key3] = variant;
          });
        },

        /**
         * Select option1
         * @param {string} value - Selected option1 value
         */
        selectOption1(value) {
          this.selectedOption1 = value;
          this.updateSelectedVariant();
          this._dispatchVariantChange();
          this._updateVariantImage();
          this._updateProductLinks();
        },

        /**
         * Select option2
         * @param {string} value - Selected option2 value
         */
        selectOption2(value) {
          this.selectedOption2 = value;
          this.updateSelectedVariant();
          this._dispatchVariantChange();
          this._updateVariantImage();
          this._updateProductLinks();
        },

        /**
         * Select option3
         * @param {string} value - Selected option3 value
         */
        selectOption3(value) {
          this.selectedOption3 = value;
          this.updateSelectedVariant();
          this._dispatchVariantChange();
          this._updateVariantImage();
          this._updateProductLinks();
        },

        /**
         * Legacy method - Select size option (maps to option1)
         * @param {string} size - Selected size value
         */
        selectSize(size) {
          this.selectOption1(size);
        },

        /**
         * Legacy method - Select flavor option (maps to option2)
         * @param {string} flavor - Selected flavor value
         */
        selectFlavor(flavor) {
          this.selectOption2(flavor);
        },

        /**
         * Update product links to include selected variant ID
         */
        _updateProductLinks() {
          if (!this.selectedVariant) return;
          const cardEl = this.$el.closest('.card-product-enhanced');
          if (!cardEl) return;
          const newHref = `${this.productUrl}?variant=${this.selectedVariant.id}`;
          cardEl.querySelectorAll('a').forEach(link => {
            const href = link.getAttribute('href');
            if (href && href.split('?')[0] === this.productUrl) {
              link.setAttribute('href', newHref);
            }
          });
        },

        /**
         * Update variant image if available
         */
        _updateVariantImage() {
          const variant = this.selectedVariant;
          if (!variant?.featured_image) return;

          const img = document.querySelector(
            `[data-product-card-image="${variant.product_id}"]`
          );
          if (!img) return;

          const imageUrl = variant.featured_image.startsWith('//')
            ? `https:${variant.featured_image}`
            : variant.featured_image;

          img.src = imageUrl;
          img.srcset = '';
        },


        /**
         * Check if variant with given options is available
         * @param {string} option1 - First option value
         * @param {string} option2 - Second option value (optional)
         * @param {string} option3 - Third option value (optional)
         * @returns {boolean} True if variant is available
         */
        _isVariantAvailable(option1, option2 = null, option3 = null) {
          const key1 = option1 || '_default';
          const key2 = option2 || '_default';
          const key3 = option3 || '_default';

          const variant = this.variantMatrix[key1]?.[key2]?.[key3];
          return variant ? variant.available : false;
        },

        /**
         * Update selected variant based on current selections
         */
        updateSelectedVariant() {
          const key1 = this.selectedOption1 || '_default';
          const key2 = this.selectedOption2 || '_default';
          const key3 = this.selectedOption3 || '_default';

          // Look up variant in matrix
          const variant = this.variantMatrix[key1]?.[key2]?.[key3];

          if (variant) {
            this.selectedVariant = variant;
          } else {
            // Fallback: find first matching variant (shouldn't happen with proper matrix)
            this.selectedVariant = this.variants.find(v => {
              const option1Match = !this.selectedOption1 || v.option1 === this.selectedOption1;
              const option2Match = !this.selectedOption2 || v.option2 === this.selectedOption2;
              const option3Match = !this.selectedOption3 || v.option3 === this.selectedOption3;
              return option1Match && option2Match && option3Match;
            }) || this.variants[0];
          }

          // Reset selling plan if new variant doesn't have the currently selected plan
          if (this.selectedSellingPlan && this.selectedVariant.selling_plan_allocations) {
            const hasSelectedPlan = this.selectedVariant.selling_plan_allocations.some(
              alloc => alloc.selling_plan_id == this.selectedSellingPlan
            );
            if (!hasSelectedPlan) {
              // Reset plan but keep purchase type preference
              this.selectedSellingPlan = '';
            }
          }

          // If variant has no selling plans, reset to one-time purchase
          if (!this.selectedVariant.selling_plan_allocations || this.selectedVariant.selling_plan_allocations.length === 0) {
            this.selectedSellingPlan = '';
            this.purchaseType = 'onetime';
          }

          // Auto-select first selling plan if subscription is selected but no plan is chosen
          if (this.purchaseType === 'subscription' && !this.selectedSellingPlan && this.selectedVariant.selling_plan_allocations?.length > 0) {
            this.selectedSellingPlan = this.selectedVariant.selling_plan_allocations[0].selling_plan_id;
          }

        },

        /**
         * Dispatch variant change event for external listeners
         */
        _dispatchVariantChange() {
          document.dispatchEvent(new CustomEvent(`eurus:card-product-variant:changed:${this.productId}`, {
            detail: {
              variant: this.selectedVariant,
              option1: this.selectedOption1,
              option2: this.selectedOption2,
              option3: this.selectedOption3,
              // Legacy aliases
              size: this.selectedOption1,
              flavor: this.selectedOption2,
              quantity: this.quantity
            }
          }));
        },

        /**
         * Check if a specific option value is available
         * Used to disable unavailable options in the UI
         * @param {string} optionKey - Option key (option1, option2, option3, etc.)
         * @param {string} optionValue - Option value to check
         * @returns {boolean} True if option has at least one available variant
         */
        isOptionAvailable(optionKey, optionValue) {
          return this.variants.some(variant => {
            if (optionKey === 'option1') {
              return variant.option1 === optionValue && variant.available;
            } else if (optionKey === 'option2') {
              // When checking option2, also consider current option1 selection
              const option1Match = !this.selectedOption1 || variant.option1 === this.selectedOption1;
              return option1Match && variant.option2 === optionValue && variant.available;
            } else if (optionKey === 'option3') {
              // When checking option3, consider current option1 and option2 selections
              const option1Match = !this.selectedOption1 || variant.option1 === this.selectedOption1;
              const option2Match = !this.selectedOption2 || variant.option2 === this.selectedOption2;
              return option1Match && option2Match && variant.option3 === optionValue && variant.available;
            }
            return false;
          });
        },

        /**
         * Increase quantity
         */
        increaseQuantity() {
          const maxQty = this._getMaxQuantity();

          if (maxQty === null || this.quantity < maxQty) {
            this.quantity++;
          }
        },

        /**
         * Decrease quantity
         */
        decreaseQuantity() {
          if (this.quantity > 1) {
            this.quantity--;
          }
        },

        /**
         * Validate quantity input
         */
        validateQuantity() {
          // Ensure minimum quantity
          if (this.quantity < 1 || isNaN(this.quantity)) {
            this.quantity = 1;
            return;
          }

          // Ensure maximum quantity if inventory tracking is enabled
          const maxQty = this._getMaxQuantity();
          if (maxQty !== null && this.quantity > maxQty) {
            this.quantity = maxQty;
          }

          // Ensure integer
          this.quantity = Math.floor(this.quantity);
        },

        /**
         * Get maximum allowed quantity based on inventory
         * @returns {number|null} Max quantity or null if unlimited
         */
        _getMaxQuantity() {
          // If no inventory management, allow unlimited
          if (!this.selectedVariant?.inventory_management) {
            return null;
          }

          // Get inventory quantity, default to unlimited if not set
          const inventoryQty = this.selectedVariant.inventory_quantity;

          // If inventory quantity is 0 or negative, no stock available
          if (inventoryQty !== undefined && inventoryQty !== null) {
            return inventoryQty;
          }

          // Fallback: allow unlimited if inventory data is missing
          return null;
        },

        /**
         * Format price for display
         * @param {number} price - Price in cents
         * @returns {string} Formatted price string
         */
        formatPrice(price) {
          if (!price) return '';

          const currency = window.Shopify?.currency?.active || 'USD';

          try {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: currency
            }).format(price / 100);
          } catch (e) {
            // Fallback formatting
            return `$${(price / 100).toFixed(2)}`;
          }
        },

        /**
         * Get variant image URL if available
         * @returns {string|null} Image URL or null
         */
        getVariantImage() {
          return this.selectedVariant?.featured_image?.src || null;
        },

        /**
         * Check if variant is on sale
         * @returns {boolean} True if variant has compare_at_price higher than price
         */
        isOnSale() {
          return this.selectedVariant?.compare_at_price > this.selectedVariant?.price;
        },

        /**
         * Calculate sale percentage
         * @returns {number} Percentage discount
         */
        getSalePercentage() {
          if (!this.isOnSale()) return 0;

          const comparePrice = this.selectedVariant.compare_at_price;
          const price = this.selectedVariant.price;

          return Math.round(((comparePrice - price) / comparePrice) * 100);
        },

        /**
         * Get low stock threshold
         * @returns {boolean} True if stock is low
         */
        isLowStock() {
          if (!this.selectedVariant?.inventory_management) return false;

          const qty = this.selectedVariant.inventory_quantity;
          return this.selectedVariant.available && qty > 0 && qty <= 5;
        },

        /**
         * Get availability message
         * @returns {string} Availability status message
         */
        getAvailabilityMessage() {
          if (!this.selectedVariant) {
            return 'Select options to check availability';
          }

          if (!this.selectedVariant.available) {
            return 'Out of stock';
          }

          if (this.isLowStock()) {
            return `Only ${this.selectedVariant.inventory_quantity} left!`;
          }

          if (this.selectedVariant.inventory_management && this.selectedVariant.inventory_quantity > 0) {
            return `${this.selectedVariant.inventory_quantity} in stock`;
          }

          return 'In stock';
        },

        /**
         * Cleanup lifecycle hook
         * Remove event listeners to prevent memory leaks
         */
        destroy() {
          // Abort all event listeners registered with AbortController
          if (this._abortController) {
            this._abortController.abort();
          }

          // Fallback: manually remove listener if AbortController not supported
          if (this._externalUpdateListener) {
            document.removeEventListener(
              `eurus:card-product:update:${this.productId}`,
              this._externalUpdateListener
            );
          }

        }
      }));
    };

    // Register on alpine:init event
    document.addEventListener('alpine:init', () => {
      registerComponent();
    });

    // Fallback: try to register after a short delay in case Alpine is already initialized
    setTimeout(() => {
      if (window.Alpine) {
        try {
          registerComponent();
        } catch (e) {
          // Already registered, ignore
        }
      }
    }, 100);

}
